import type {
  InventoryApiItem,
  InventoryApiPagination,
  InventoryApiResponse,
  InventoryApiView,
  InventoryDeviceStatus,
  InventoryDeviceSummary,
  InventoryItemRow,
  InventoryLocationEntry,
  InventoryPagination,
  InventorySortKey,
  InventoryStatus,
  InventorySubItem,
  InventoryTeam,
  InventoryUnifiedResponse,
  InventoryViewMode,
} from '../type';

export const PAGE_SIZE = 50;
export const ITEM_PAGE_SIZE = 10;
export const SUBGROUPS_PAGE_SIZE = 10;
export const DEFAULT_INVENTORY_VIEW: InventoryViewMode = 'per_sku';
export const INVENTORY_SORT_DEFAULTS: Record<
  InventoryViewMode,
  InventorySortKey
> = {
  per_sku: 'stock_vs_required_state',
  per_device: 'location',
};

export const EMPTY_INVENTORY_RESPONSE: InventoryUnifiedResponse = {
  mode: 'flat',
  groupBy: '',
  subGroupBy: '',
  items: [],
  pagination: {
    page: 0,
    pageSize: PAGE_SIZE,
    totalPages: 1,
    totalCount: 0,
  },
};

export const mapPagination = (
  pagination?: InventoryApiPagination
): InventoryPagination => ({
  page: Number(pagination?.page ?? 0),
  pageSize: Number(pagination?.page_size ?? PAGE_SIZE),
  totalPages: Number(pagination?.total_pages ?? 1),
  totalCount: Number(pagination?.total_count ?? 0),
});

const normalizeStatus = (state?: string | null): InventoryStatus => {
  switch (state) {
    case 'low':
      return 'Low';
    case 'high':
      return 'High';
    default:
      return '';
  }
};

const normalizeDeviceStatus = (
  state?: string | null
): InventoryDeviceStatus => {
  const normalized = (state ?? '').toLowerCase().replace(/[_-]/g, ' ');

  switch (normalized) {
    case 'unused':
      return 'Unused';
    case 'activated':
    case 'active':
      return 'Activated';
    case 'returned':
    case 'return':
      return 'Returned';
    default:
      return '';
  }
};

const mapDeviceSummary = (
  summary?: Partial<InventoryDeviceSummary> | null
): InventoryDeviceSummary => ({
  total_devices: Number(summary?.total_devices ?? 0),
  unused: Number(summary?.unused ?? 0),
  activated: Number(summary?.activated ?? 0),
  returned: Number(summary?.returned ?? 0),
});

const getDeviceIdentifier = (item: InventoryApiItem) => item.device_id;

export const shouldIncludeItemForView = (
  item: InventoryApiItem,
  view: InventoryViewMode
) => view !== 'per_device' || Boolean(getDeviceIdentifier(item));

export const mapInventoryViewToApi = (
  view: InventoryViewMode
): InventoryApiView => (view === 'per_device' ? 'device' : 'sku');

export const dedupeItems = (items: InventoryItemRow[]) => {
  const seen = new Set<string>();

  return items.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
};

export const dedupeSubgroups = <
  T extends {
    id: string;
  },
>(
  subgroups: T[]
) => {
  const seen = new Set<string>();

  return subgroups.filter((subgroup) => {
    if (seen.has(subgroup.id)) return false;
    seen.add(subgroup.id);
    return true;
  });
};

export const dedupeGroups = <
  T extends {
    id: string;
  },
>(
  groups: T[]
) => {
  const seen = new Set<string>();

  return groups.filter((group) => {
    if (seen.has(group.id)) return false;
    seen.add(group.id);
    return true;
  });
};

export const mapInventoryItem = (
  item: InventoryApiItem,
  view: InventoryViewMode
): InventoryItemRow => {
  const teams: InventoryTeam[] = Array.isArray(item.teams)
    ? item.teams.map((team) => ({
        id: team.id,
        name: team.name,
        color: team.color,
      }))
    : [];

  const locationNames: string[] = Array.isArray(item.location?.names)
    ? item.location?.names
    : [];

  const locations: InventoryLocationEntry[] = locationNames.map(
    (name, index) => ({
      id: `${item.id}-location-${index}`,
      name,
    })
  );

  const subItems: InventorySubItem[] = Array.isArray(item.sub_items)
    ? item.sub_items.map((subItem) => ({
        sku: subItem.sku,
        quantity: subItem.quantity,
      }))
    : [];

  if (view === 'per_device') {
    const deviceStatus = normalizeDeviceStatus(item.status_label);
    const deviceId = item.device_id as string;
    const primaryTeam = item.team
      ? [
          {
            id: item.team.id,
            name: item.team.name,
            color: item.team.color,
          },
        ]
      : [];

    return {
      id: item.id,
      inventoryId: item.inventory_record_id as string,
      sku: item.sku,
      description: item.product?.description ?? '',
      rowType: 'item',
      __depth: 0,
      teams: primaryTeam,
      teamTooltip: primaryTeam.map((team) => ({
        label: 'Team',
        value: team.name,
      })),
      location: item.site_id as string,
      locations: [],
      stock: 0,
      required: null,
      status: '',
      stockPlanningCurrent: 0,
      stockPlanningTotal: null,
      updatedAt: item.last_updated ?? '',
      deviceId,
      serialNumber: item.serial_number ?? undefined,
      siteId: item.site_id as string,
      siteName: item.site_name ?? '',
      deviceStatus,
      shippedIn: item.shipped_in ?? undefined,
      activatedAt: item.activated ?? undefined,
      returnedAt: item.returned ?? undefined,
    };
  }

  return {
    id: item.record_type === 'kit' ? `kits_items_${item.id}` : item.id,
    inventoryId: item.inventory_record_id as string,
    sku: item.sku ?? '--',
    description: item.description ?? '',
    rowType: 'item',
    __depth: 0,

    recordType: item.record_type ?? 'item',
    hasNotification: !!item.has_notification,
    can_save_notification: item?.can_save_notification,
    subItems,
    skuTooltip: subItems.map((subItem) => ({
      label: subItem.sku ?? '--',
      value:
        subItem.quantity === null || subItem.quantity === undefined
          ? '--'
          : String(subItem.quantity),
    })),

    location: item.location_label ?? item.location?.label ?? '--',
    locations,
    locationTooltip: locationNames.map((name) => ({
      label: name,
      value: '',
    })),

    teams,
    teamTooltip: teams.map((team) => ({
      label: 'Team',
      value: team.name,
    })),

    stock: item.current_stock ?? item.stock_vs_required?.stock ?? 0,
    required:
      item.reorder_target ?? item.stock_vs_required?.reorder_target ?? null,
    status: normalizeStatus(
      item.stock_state ?? item.stock_vs_required?.state
    ) as InventoryStatus,

    stockVsRequiredText: item.stock_vs_required?.text ?? '--',
    stockVsRequiredTooltip: [
      {
        label: 'Current stock',
        value: String(item.current_stock ?? item.stock_vs_required?.stock ?? 0),
      },
      {
        label: 'Reorder target',
        value:
          item.reorder_target ?? item.stock_vs_required?.reorder_target ?? '--',
      },
    ],

    stockPlanningCurrent:
      item.shipped_to_date ?? item.stock_planning?.shipped_to_date ?? 0,
    stockPlanningTotal:
      item.total_required ?? item.stock_planning?.total_required ?? null,
    stockPlanningText: item.stock_planning?.text ?? '0',
    stockPlanningTooltip: [
      {
        label: 'Shipped to date',
        value: String(
          item.shipped_to_date ?? item.stock_planning?.shipped_to_date ?? 0
        ),
      },
      {
        label: 'Total required',
        value:
          item.total_required ?? item.stock_planning?.total_required ?? '--',
      },
    ],

    updatedAt: item.last_updated ?? item.last_updated_at ?? '',
    shippedIn: item.shipped_in ?? undefined,
    returnedAt: item.returned ?? undefined,

    subItemCount: item.record_type === 'kit' ? subItems.length : 0,
    structureTooltip: [
      {
        label: 'Record type',
        value: item.record_type ?? '--',
      },
    ],
  };
};

export const mapSubgroup = (
  subgroup: {
    id: string;
    name: string;
    color?: string | null;
    count: number;
    items?: InventoryApiItem[];
    pagination?: InventoryApiPagination;
  },
  view: InventoryViewMode
) => ({
  id: subgroup.id,
  name: subgroup.name,
  color: subgroup.color ?? undefined,
  count: subgroup.count,
  items: (subgroup.items ?? [])
    .filter((item) => shouldIncludeItemForView(item, view))
    .map((item) => mapInventoryItem(item, view)),
  pagination: mapPagination(subgroup.pagination),
});

export const mapGroup = (
  group: {
    id: string;
    name: string;
    count: number;
    items?: InventoryApiItem[];
    pagination?: InventoryApiPagination;
  },
  view: InventoryViewMode
) => ({
  id: group.id,
  name: group.name,
  count: group.count,
  items: (group.items ?? [])
    .filter((item) => shouldIncludeItemForView(item, view))
    .map((item) => mapInventoryItem(item, view)),
  pagination: mapPagination(group.pagination),
});

export const mapGroupWithSubgroups = (
  group: {
    id: string;
    name: string;
    count: number;
    items?: InventoryApiItem[];
    subgroups?: Array<{
      id: string;
      name: string;
      color?: string | null;
      count: number;
      items?: InventoryApiItem[];
      pagination?: InventoryApiPagination;
    }>;
    pagination?: InventoryApiPagination;
  },
  view: InventoryViewMode
) => ({
  id: group.id,
  name: group.name,
  count: group.count,
  items: (group.items ?? [])
    .filter((item) => shouldIncludeItemForView(item, view))
    .map((item) => mapInventoryItem(item, view)),
  subgroups: (group.subgroups ?? []).map((subgroup) =>
    mapSubgroup(subgroup, view)
  ),
  pagination: mapPagination(group.pagination),
});

export const buildInventoryUnifiedResponse = (
  apiPayload: InventoryApiResponse,
  view: InventoryViewMode
): InventoryUnifiedResponse => {
  if (apiPayload.mode === 'flat') {
    return {
      mode: 'flat',
      groupBy: apiPayload.group_by || '',
      subGroupBy: apiPayload.sub_group_by || '',
      summary: mapDeviceSummary(apiPayload.summary),
      items: (apiPayload.items ?? [])
        .filter((item) => shouldIncludeItemForView(item, view))
        .map((item) => mapInventoryItem(item, view)),
      pagination: mapPagination(apiPayload.pagination),
    };
  }

  if (apiPayload.mode === 'group') {
    return {
      mode: 'group',
      groupBy: apiPayload.group_by || '',
      subGroupBy: apiPayload.sub_group_by || '',
      summary: mapDeviceSummary(apiPayload.summary),
      groups: (apiPayload.groups ?? []).map((group) => mapGroup(group, view)),
      pagination: mapPagination(apiPayload.pagination),
    };
  }

  return {
    mode: 'group_subgroup',
    groupBy: apiPayload.group_by || '',
    subGroupBy: apiPayload.sub_group_by || '',
    summary: mapDeviceSummary(apiPayload.summary),
    groups: (apiPayload.groups ?? []).map((group) =>
      mapGroupWithSubgroups(group, view)
    ),
    pagination: mapPagination(apiPayload.pagination),
  };
};
